import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Props para el componente NumberInput
 */
interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange'> {
  /** Valor controlado (string o number) */
  value?: string | number;
  /** Valor inicial en modo no controlado */
  defaultValue?: string | number;
  /** Callback que se ejecuta al cambiar el input, emite siempre el valor crudo */
  onChange?: (rawValue: string) => void;
  /** Callback que se ejecuta al perder foco o presionar Enter, emite el valor numérico validado */
  onAccept?: (value: number | null) => void;
  /** Si es true, muestra el ícono de moneda y formatea como currency */
  currency?: boolean;
  /** Cantidad de decimales permitidos/formateados. Default: 2 */
  decimals?: number;
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Clase CSS adicional para el contenedor */
  containerClassName?: string;
}

/**
 * Referencia expuesta del componente
 */
export interface NumberInputRef {
  focus: () => void;
  blur: () => void;
  getValue: () => number | null;
  getRawValue: () => string;
}

/**
 * Formatea un número según el formato argentino
 * @param value - Valor numérico
 * @param decimals - Cantidad de decimales
 * @param currency - Si debe mostrar símbolo de moneda
 */
const formatNumber = (value: number, decimals: number = 2, currency: boolean = false): string => {
  const formatted = new Intl.NumberFormat('es-AR', {
    style: 'decimal',
    currency: currency ? 'ARS' : undefined,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

  return formatted;
};

/**
 * Convierte un string formateado a número
 * @param value - String formateado (es-AR, ej. "1.234,56") o crudo (ej. "1234.56")
 */
export const parseFormattedNumber = (value: string): number | null => {
  if (!value || value.trim() === '') return null;

  // Remover símbolos de moneda y espacios
  let cleanValue = value.replace(/[$\s]/g, '');

  // Reemplazar coma decimal por punto
  cleanValue = cleanValue.replace(',', '.');

  // Remover puntos de separadores de miles (mantener solo el último punto si es decimal)
  const parts = cleanValue.split('.');
  if (parts.length > 2) {
    // Hay múltiples puntos, los primeros son separadores de miles
    const integerPart = parts.slice(0, -1).join('').replace(/\./g, '');
    const decimalPart = parts[parts.length - 1];
    cleanValue = `${integerPart}.${decimalPart}`;
  }
  // Con un solo punto es el separador decimal, tenga los dígitos que tenga: un
  // porcentaje con 4 decimales se formatea "20,0000" y debe leerse como 20, no
  // como 200000. Los miles siempre se agrupan de a 3 y con decimales llevan coma.

  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Valida si un caracter es permitido en el input numérico
 */
const isValidChar = (char: string, currentValue: string, cursorPosition: number, decimals: number = 2): boolean => {
  // Permitir números
  if (/[0-9]/.test(char)) {
    // Si hay punto decimal, verificar que no exceda la cantidad de decimales permitidos
    const decimalIndex = currentValue.indexOf('.');
    if (decimalIndex !== -1) {
      // Solo validar límite de decimales si el cursor está después del punto decimal
      if (cursorPosition > decimalIndex) {
        const decimalsAfterPoint = currentValue.length - decimalIndex - 1;
        if (decimalsAfterPoint >= decimals) {
          return false; // Ya se alcanzó el límite de decimales
        }
      }
    }
    return true;
  }

  // Permitir punto decimal solo si no existe ya uno
  if (char === '.' && !currentValue.includes('.')) return true;

  // Permitir signo negativo solo al inicio
  if (char === '-' && currentValue.length === 0) return true;

  return false;
};

/**
 * Componente NumberInput reutilizable
 */
export const NumberInput = forwardRef<NumberInputRef, NumberInputProps>(({
  value,
  defaultValue,
  onChange,
  onAccept,
  currency = false,
  decimals = 2,
  min,
  max,
  containerClassName,
  className,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  disabled,
  ...rest
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState<string>(() => {
    const initialValue = value !== undefined ? value : defaultValue;
    if (initialValue) {
      const numericValue = typeof initialValue === 'number' ? initialValue : parseFormattedNumber(String(initialValue));
      if (numericValue !== null) {
        return formatNumber(numericValue, decimals, currency);
      }
    }
    return '';
  });
  const [isFormatted, setIsFormatted] = useState(() => {
    const initialValue = value !== undefined ? value : defaultValue;
    return initialValue ? true : false;
  });
  const [isFocused, setIsFocused] = useState(false);

  // Sincronizar con prop value cuando cambia
  useEffect(() => {
    if (value !== undefined) {
      const newValue = String(value);
      if (newValue !== internalValue) {
        const numericValue = typeof value === 'number' ? value : parseFormattedNumber(String(value));
        if (numericValue !== null) {
          const formattedValue = formatNumber(numericValue, decimals, currency);
          setInternalValue(formattedValue);
          setIsFormatted(true);
        } else {
          setInternalValue(newValue);
          setIsFormatted(false);
        }
      }
    }
  }, [value, decimals, currency]);

  // Exponer métodos a través de ref
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
    getValue: () => parseFormattedNumber(internalValue),
    getRawValue: () => internalValue,
  }));

  /**
   * Valida y ajusta el valor según los límites min/max
   */
  const validateValue = (numericValue: number | null): number | null => {
    if (numericValue === null) return null;

    let validated = numericValue;

    if (min !== undefined && validated < min) {
      validated = min;
    }
    if (max !== undefined && validated > max) {
      validated = max;
    }

    return validated;
  };

  /**
   * Maneja el cambio en el input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Si está formateado, permitir que el usuario edite libremente
    if (isFormatted) {
      setIsFormatted(false);
    }

    setInternalValue(inputValue);
    onChange?.(inputValue);
  };

  /**
   * Maneja el evento de tecla presionada
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir teclas de control
    const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

    if (controlKeys.includes(e.key)) {
      if (e.key === 'Enter') {
        handleAccept();
      }
      onKeyDown?.(e);
      return;
    }

    // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      onKeyDown?.(e);
      return;
    }

    // Validar caracteres permitidos
    const cursorPosition = inputRef.current?.selectionStart || 0;
    if (!isValidChar(e.key, internalValue, cursorPosition, decimals)) {
      e.preventDefault();
      return;
    }

    onKeyDown?.(e);
  };

  /**
   * Maneja el evento de foco
   */
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);

    // Si está formateado, convertir a valor crudo para edición
    if (isFormatted) {
      const numericValue = parseFormattedNumber(internalValue);
      const rawValue = numericValue !== null ? String(numericValue) : '';
      setInternalValue(rawValue);
      setIsFormatted(false);

      // Seleccionar todo el texto al enfocar
      setTimeout(() => {
        inputRef.current?.select();
      }, 0);
    }

    onFocus?.(e);
  };

  /**
   * Maneja la pérdida de foco y el evento Accept
   */
  const handleAccept = () => {
    const numericValue = parseFormattedNumber(internalValue);
    const validatedValue = validateValue(numericValue);

    if (validatedValue !== null) {
      // Formatear el valor para mostrar
      const formattedValue = formatNumber(validatedValue, decimals, currency);
      setInternalValue(formattedValue);
      setIsFormatted(true);
    } else if (internalValue.trim() === '') {
      // Si está vacío, mantener vacío
      setInternalValue('');
      setIsFormatted(false);
    } else {
      // Valor inválido, mantener el valor actual pero no formatear
      setIsFormatted(false);
    }

    onAccept?.(validatedValue);
  };

  /**
   * Maneja la pérdida de foco
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    handleAccept();
    onBlur?.(e);
  };

  // Valor a mostrar en el input
  const displayValue = internalValue;

  return (
    <div className={cn("relative", containerClassName)}>
      {currency && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          currency && "pl-10",
          className
        )}
        aria-label={currency ? "Campo de moneda" : "Campo numérico"}
        aria-describedby={min !== undefined || max !== undefined ? `${rest.id}-constraints` : undefined}
        {...rest}
      />

      {/* Información de restricciones para screen readers */}
      {(min !== undefined || max !== undefined) && (
        <div id={`${rest.id}-constraints`} className="sr-only">
          {min !== undefined && max !== undefined && `Valor entre ${min} y ${max}`}
          {min !== undefined && max === undefined && `Valor mínimo ${min}`}
          {min === undefined && max !== undefined && `Valor máximo ${max}`}
        </div>
      )}
    </div>
  );
});

NumberInput.displayName = "NumberInput";

export default NumberInput;